import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { IconEdit, IconPhoto } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { Role } from '../../../types/src'
import { AppModal } from '../AppModal'

// Simple get function for nested paths (e.g., 'address.street')
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

// Simple set function for nested paths
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split('.')
  const result = { ...obj }
  let current = result

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    current[key] = { ...current[key] }
    current = current[key]
  }

  current[keys[keys.length - 1]] = value
  return result
}

export interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'number'
  unit?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatDisplay?: (value: any) => string | null
  visibilityRoles?: Role[]
}

interface EditableSectionProps {
  title: string
  fields: FieldConfig[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entity: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate: (values: any) => Promise<void>
  isUpdating?: boolean
  canEdit?: boolean
  attachmentSlot?: ReactNode
  attachmentAddButton?: ReactNode
}

function DetailItem({
  label,
  value,
}: {
  label: string
  value: string | number | null | undefined
}) {
  return (
    <Box>
      <Text size="sm" c="dimmed" fw={500}>
        {label}
      </Text>
      <Text size="md" mt={2}>
        {value || 'Ei tietoa'}
      </Text>
    </Box>
  )
}

export function EditableSection({
  title,
  fields,
  entity,
  onUpdate,
  isUpdating,
  canEdit = true,
  attachmentSlot,
  attachmentAddButton,
}: EditableSectionProps) {
  const [opened, { open, close }] = useDisclosure(false)

  const form = useForm({
    initialValues: fields.reduce(
      (acc, field) => {
        const value = getNestedValue(entity, field.key)
        return setNestedValue(acc, field.key, value ?? '')
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {} as Record<string, any>,
    ),
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (values: any) => {
    await onUpdate(values)
    close()
  }

  const handleOpen = () => {
    form.setValues(
      fields.reduce(
        (acc, field) => {
          const value = getNestedValue(entity, field.key)
          return setNestedValue(acc, field.key, value ?? '')
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as Record<string, any>,
      ),
    )
    form.resetDirty()
    open()
  }

  const handleClose = () => {
    if (form.isDirty()) {
      modals.openConfirmModal({
        title: 'Tallentamattomat muutokset',
        centered: true,
        children: (
          <Text>
            Sinulla on tallentamattomia muutoksia. Haluatko varmasti sulkea
            lomakkeen tallentamatta?
          </Text>
        ),
        labels: { confirm: 'Sulje tallentamatta', cancel: 'Peruuta' },
        confirmProps: { color: 'red' },
        onConfirm: close,
      })
    } else {
      close()
    }
  }

  return (
    <>
      <Card>
        <Group justify="space-between" mb="md">
          <Title order={4}>{title}</Title>
          {canEdit && (
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconEdit size={16} />}
              onClick={handleOpen}
            >
              Muokkaa
            </Button>
          )}
        </Group>
        <Stack gap="md">
          {fields.map((field) => {
            const value = getNestedValue(entity, field.key)
            const displayValue = field.formatDisplay
              ? field.formatDisplay(value)
              : field.unit && value
                ? `${value} ${field.unit}`
                : value
            return (
              <DetailItem
                key={field.key}
                label={field.label}
                value={displayValue}
              />
            )
          })}
        </Stack>

        {attachmentSlot && (
          <>
            <Divider my="md" />
            <Box>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <IconPhoto size={16} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" fw={500} c="dimmed">
                    Kuvat
                  </Text>
                </Group>
                {attachmentAddButton}
              </Group>
              {attachmentSlot}
            </Box>
          </>
        )}
      </Card>

      <AppModal
        opened={opened}
        onClose={handleClose}
        title={`Muokkaa - ${title}`}
        size="md"
        cancelProps={{
          label: 'Peruuta',
        }}
        confirmProps={{
          label: 'Tallenna',
          onClick: form.onSubmit(handleSubmit),
          loading: isUpdating,
        }}
      >
        <Stack gap="md">
          {fields.map((field) => {
            if (field.type === 'number') {
              return (
                <NumberInput
                  key={field.key}
                  label={field.label}
                  {...form.getInputProps(field.key)}
                />
              )
            }
            return (
              <TextInput
                key={field.key}
                label={field.label}
                {...form.getInputProps(field.key)}
              />
            )
          })}
        </Stack>
      </AppModal>
    </>
  )
}
